import React, { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from './stores/authStore'
import { usePermissionStore } from './stores/permissionStore'
import { authApi } from './lib/api/auth'
import type { Role } from './types'
import LoadingSpinner from './components/ui/LoadingSpinner'

import LandingPage from './pages/LandingPage'
import SignIn from './pages/auth/SignIn'
import SignUp from './pages/auth/SignUp'
import ForgotPassword from './pages/auth/ForgotPassword'
import SetPassword from './pages/auth/SetPassword'
import ResetPassword from './pages/auth/ResetPassword'
import InterviewerApply from './pages/InterviewerApply'
import PolicyPage from './pages/PolicyPage'
import AboutUs from './pages/AboutUs'

const ClientDashboard      = React.lazy(() => import('./pages/client/ClientDashboard'))
const InterviewerDashboard = React.lazy(() => import('./pages/interviewer/InterviewerDashboard'))
const ZoomCallback         = React.lazy(() => import('./pages/interviewer/ZoomCallback'))
const AdminDashboard       = React.lazy(() => import('./pages/admin/AdminDashboard'))

// ── Protected Route ──────────────────────────────────────────────────────────
interface ProtectedRouteProps {
  children: React.ReactNode
  allowedRoles: Role[]
}

function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, token } = useAuthStore()

  if (!token || !user) {
    return <Navigate to="/signin" replace />
  }

  if (!allowedRoles.includes(user.role)) {
    return <Navigate to={`/${user.role}`} replace />
  }

  return <>{children}</>
}

// ── Public Route (redirect if authenticated) ─────────────────────────────────
interface PublicRouteProps {
  children: React.ReactNode
}

function PublicAuthRoute({ children }: PublicRouteProps) {
  const { user, logout } = useAuthStore()
  if (user) {
    if (!user.role) { logout(); return <>{children}</> }
    return <Navigate to={user.role === 'client' ? '/client/profile' : `/${user.role}`} replace />
  }
  return <>{children}</>
}

// ── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  const { token, user, setUser, logout } = useAuthStore()
  const { load: loadPermissions, clear: clearPermissions } = usePermissionStore()
  const location = useLocation()

  // Validate token on mount — use React Query so the result is cached
  const { isLoading: isHydrating } = useQuery({
    queryKey: ['me'],
    queryFn: () =>
      authApi.me().then((u) => { setUser(u); return u }).catch((e) => { logout(); clearPermissions(); throw e }),
    enabled: !!token,
    retry: false,
  })

  // Load RBAC permissions whenever an admin logs in
  useEffect(() => {
    if (user?.role === 'admin' && token) {
      loadPermissions()
    } else if (!token) {
      clearPermissions()
    }
  }, [user?.role, token, loadPermissions, clearPermissions])

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  // While token exists but we haven't hydrated the user yet, show a spinner
  // to prevent a flash of the signin page.
  if (token && isHydrating) {
    return <LoadingSpinner />
  }

  return (
    <React.Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/about-us" element={<AboutUs />} />
        <Route path="/apply-interviewer" element={<InterviewerApply />} />
        <Route path="/terms-and-conditions" element={<PolicyPage policyKey="terms_conditions" />} />
        <Route path="/payment-policy" element={<PolicyPage policyKey="payment_policy" />} />
        <Route path="/privacy-policy" element={<PolicyPage policyKey="privacy_policy" />} />
        <Route path="/support" element={<PolicyPage policyKey="support_page" />} />
        <Route path="/zoom-docs" element={<PolicyPage policyKey="zoom_documentation" />} />

        {/* Auth routes — redirect away if already logged in */}
        <Route path="/signin" element={<PublicAuthRoute><SignIn /></PublicAuthRoute>} />
        <Route path="/signup" element={<PublicAuthRoute><SignUp /></PublicAuthRoute>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/set-password" element={<SetPassword />} />

        {/* Zoom OAuth callback — matches ZOOM_REDIRECT_URI path on the frontend domain */}
        <Route
          path="/api/zoom/oauth/callback"
          element={
            <ProtectedRoute allowedRoles={['interviewer']}>
              <ZoomCallback />
            </ProtectedRoute>
          }
        />
        <Route
          path="/interviewer/zoom/callback"
          element={
            <ProtectedRoute allowedRoles={['interviewer']}>
              <ZoomCallback />
            </ProtectedRoute>
          }
        />

        {/* Protected portals */}
        <Route
          path="/client/*"
          element={
            <ProtectedRoute allowedRoles={['client']}>
              <ClientDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/interviewer/*"
          element={
            <ProtectedRoute allowedRoles={['interviewer']}>
              <InterviewerDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Suspense>
  )
}
